<template>
  <section class="credits-request credit-active">
    <div class="credit-active__heading">
      <span class="material-icons-sharp credits-request__icon" aria-hidden="true">payments</span>
      <h2 class="credits-request__title">{{ $t('credits.active_amount') }}</h2>
      <p class="credit-active__amount"><credit-money-text :text="data.amount" /></p>
    </div>

    <div class="credit-active__progress">
      <div class="credit-active__progress-heading">
        <span>{{ $t('credits.active_progress') }}</span>
        <b>{{ completedCount }} <span>/ {{ totalCount }}</span></b>
      </div>
      <div v-if="totalCount > 0" class="credit-active__track" role="progressbar"
           :class="{ 'credit-active__track--advancing': progressAdvancing }"
           :aria-label="$t('credits.active_progress')" :aria-valuemin="0"
           :aria-valuemax="totalCount" :aria-valuenow="completedCount">
        <span class="credit-active__fill"
              :class="{ 'credit-active__fill--complete': completedCount === totalCount,
                        'credit-active__fill--preparing': progressPreparing,
                        'credit-active__fill--advancing': progressAdvancing }"
              :style="{ width: displayedProgressPercentage + '%' }">
          <span v-if="progressAdvancing" class="credit-active__wind" aria-hidden="true">
            <svg viewBox="0 0 44 28" fill="none">
              <path d="M9 5h18c4 0 6 2 7 5M2 14h29M13 23h13c4 0 6-2 8-5" />
              <circle cx="35" cy="14" r="3" />
            </svg>
          </span>
        </span>
      </div>
      <p v-if="data.streak > 0" class="credit-active__streak">
        {{ $t('credits.streak', { count: data.streak }, data.streak) }}
      </p>
    </div>

    <div v-if="data.collection_fx_paused" class="credit-active__notice" role="status">
      <p>{{ data.collection_fx_pause_message }}</p>
    </div>
    <div v-if="data.overdue" class="credit-active__notice credit-active__notice--overdue" role="note">
      <img :src="statusIcons.overdue" class="credit-status-icon" width="20" height="20" alt="" aria-hidden="true" />
      <p><credit-money-text :text="$t('credits.overdue_banner', { amount: data.overdue_amount })" /></p>
    </div>
    <div v-else-if="data.due_today_notice" class="credit-active__notice credit-active__notice--due-today" role="note">
      <img :src="statusIcons.due_today" class="credit-status-icon" width="20" height="20" alt="" aria-hidden="true" />
      <p><credit-money-text :text="$t('credits.due_today_banner', { amount: data.due_today_amount })" /></p>
    </div>
    <div v-if="data.admin_fee_pending" class="credit-active__notice" role="note">
      <span class="material-icons-sharp" aria-hidden="true">info</span>
      <p><credit-money-text :text="data.admin_fee_pending.message" /></p>
    </div>
    <div v-if="collectionNotice" class="credit-active__notice" role="status">
      <p>{{ collectionNotice }}</p>
    </div>

    <div v-if="overdueInstallments.length" class="credit-active__overdue">
      <span class="credit-status-pill credit-active__status" data-payment-state="overdue">
        <img :src="statusIcons.overdue" class="credit-status-icon" width="20" height="20" alt="" aria-hidden="true" />
        <span>{{ $t('credits.installment_overdue') }}</span>
      </span>
      <table class="credit-active__overdue-table">
        <thead>
          <tr>
            <th scope="col">{{ $t('credits.overdue_number') }}</th>
            <th scope="col">{{ $t('credits.overdue_due_on') }}</th>
            <th scope="col">{{ $t('credits.overdue_value') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cuota in overdueInstallments" :key="cuota.number">
            <th scope="row">{{ cuota.number }}</th>
            <td>
              <span class="credit-active__due-date">{{ cuota.due_on }}</span>
            </td>
            <td><credit-money-text :text="cuota.amount" /></td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else-if="data.admin_fee_pending || data.installment_total || data.next_due_on"
         class="credit-active__upcoming">
      <span v-if="data.due_today" class="credit-status-pill credit-active__status" data-payment-state="due_today">
        <img :src="statusIcons.due_today" class="credit-status-icon" width="20" height="20" alt="" aria-hidden="true" />
        <span>{{ $t('credits.installment_due_today') }}</span>
      </span>
      <dl class="credit-active__upcoming-fields">
        <template v-if="data.admin_fee_pending">
          <div class="credit-active__datum">
            <dt>{{ data.admin_fee_pending.amount_label }}</dt>
            <dd><credit-money-text :text="data.admin_fee_pending.amount" /></dd>
          </div>
          <div class="credit-active__datum">
            <dt>{{ data.admin_fee_pending.next_due_label }}</dt>
            <dd>
              <span class="credit-active__due-date">{{ data.admin_fee_pending.next_due_on }}</span>
            </dd>
          </div>
        </template>
        <template v-else>
          <div v-if="data.installment_total" class="credit-active__datum">
            <dt>{{ data.installment_label || $t('credits.active_installment') }}</dt>
            <dd><credit-money-text :text="data.installment_total" /></dd>
          </div>
          <div v-if="data.next_due_on" class="credit-active__datum">
            <dt>{{ $t('credits.next_due') }}</dt>
            <dd>
              <span class="credit-active__due-date">{{ data.next_due_on }}</span>
            </dd>
          </div>
        </template>
      </dl>
    </div>

    <div class="credits-request__actions credit-active__actions">
      <!-- Con fondos suficientes el botón lanza el cobro (POST, mismo endpoint
           que "Pagar ahora" del detalle): "Regularizar" si hay mora y "Pagar
           ahora" si lo exigible es la cuota de hoy. Sin fondos, lleva a abonar. -->
      <form v-if="actionsAvailable && collectWithBalance" :action="collectUrl" method="post"
            class="credit-active__regularize">
        <input type="hidden" name="authenticity_token" :value="csrfToken" />
        <input type="hidden" name="from" value="credits_index" />
        <!-- data-disable-with (rails-ujs): un doble clic no encola dos cobros. -->
        <button type="submit" class="credits-cta credits-request__view"
                :class="{ 'credit-active__regularize-cta': data.overdue }"
                :data-disable-with="$t('credits.paying')">
          {{ $t(data.overdue ? 'credits.regularize' : 'credits.pay_now') }}
        </button>
      </form>
      <a v-else-if="actionsAvailable" :href="topUpUrl" class="credits-cta credits-request__view"
         :class="{ 'credit-active__regularize-cta': data.overdue }">
        {{ $t(data.overdue ? 'credits.regularize' : 'credits.top_up') }}
      </a>
      <a v-if="creditUrl" :href="creditUrl"
         :class="data.collection_fx_paused ? 'credits-cta credits-request__view' : 'credit-active__details'">
        {{ $t('credits.active_details') }}
      </a>
    </div>
  </section>
</template>

<script>
import CreditMoneyText from './credit-money-text.vue';
import overdueIcon from '../../../../assets/images/credit-status-overdue.svg';
import dueTodayIcon from '../../../../assets/images/credit-status-due-today.svg';

const CELEBRATION_EVENT = 'credits:installment-celebration';
const PROGRESS_DURATION = 1200;
const positiveId = value => (typeof value === 'number' || (typeof value === 'string' && /^[1-9]\d*$/.test(value))) &&
  Number.isSafeInteger(Number(value)) && Number(value) > 0;

export default {
  name: 'CreditActiveCard',
  components: { CreditMoneyText },
  props: {
    data: { type: Object, required: true },
    topUpUrl: { type: String, required: true },
    creditUrl: { type: String, default: '' },
    collectUrl: { type: String, default: '' }
  },
  data() {
    return {
      statusIcons: { overdue: overdueIcon, due_today: dueTodayIcon },
      visualProgressPercentage: null,
      progressPreparing: false,
      progressAdvancing: false,
      progressFrame: null,
      progressTimer: null,
      celebratedInstallments: new Set()
    };
  },
  mounted() {
    document.addEventListener(CELEBRATION_EVENT, this.celebrateInstallment);
  },
  beforeUnmount() {
    document.removeEventListener(CELEBRATION_EVENT, this.celebrateInstallment);
    this.cancelProgressAnimation();
  },
  watch: {
    'data.loan_id'() { this.cancelProgressAnimation(); },
    completedCount() { this.cancelProgressAnimation(); },
    totalCount() { this.cancelProgressAnimation(); }
  },
  computed: {
    totalCount() {
      const count = Number(this.data.total_count);
      return Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
    },
    completedCount() {
      const count = Number(this.data.paid_count);
      return Number.isFinite(count) ? Math.min(this.totalCount, Math.max(0, Math.trunc(count))) : 0;
    },
    progressPercentage() {
      return this.totalCount > 0 ? (this.completedCount / this.totalCount) * 100 : 0;
    },
    displayedProgressPercentage() {
      return this.visualProgressPercentage === null ? this.progressPercentage : this.visualProgressPercentage;
    },
    confirmingAdministration() {
      return this.data.admin_fee_pending && this.data.admin_fee_pending.confirming_payment;
    },
    // Cobro anterior por confirmar o cobro en curso: la nota reemplaza los
    // botones. La administración en confirmación ya trae su propia nota.
    collectionNotice() {
      const nota = this.data.collection_notice;
      return !this.confirmingAdministration && typeof nota === 'string' ? nota : '';
    },
    actionsAvailable() {
      return !this.data.collection_fx_paused && !this.confirmingAdministration && !this.collectionNotice;
    },
    overdueInstallments() {
      return this.data.overdue && Array.isArray(this.data.overdue_installments) ? this.data.overdue_installments : [];
    },
    // La mora manda: con cuotas vencidas el cobro es "Regularizar"; sin mora,
    // "Pagar ahora" cobra la cuota que vence hoy. Ambos exigen fondos.
    collectWithBalance() {
      if (!this.collectUrl) return false;
      return Boolean(this.data.overdue ? this.data.regularize_with_balance : this.data.pay_now_with_balance);
    },
    csrfToken() {
      const meta = document.querySelector('meta[name="csrf-token"]');
      return meta ? meta.getAttribute('content') : '';
    }
  },
  methods: {
    celebrateInstallment(event) {
      const detail = event.detail;
      if (!detail || detail.kind !== 'installment_paid' || !positiveId(detail.installment_id) ||
          !positiveId(detail.loan_id) || !positiveId(this.data.loan_id) ||
          Number(detail.loan_id) !== Number(this.data.loan_id) || this.completedCount === 0) return;

      const eventId = `${detail.loan_id}:${detail.installment_id}`;
      if (this.celebratedInstallments.has(eventId) || this.progressPreparing || this.progressAdvancing) return;
      this.celebratedInstallments.add(eventId);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Only the painted width replays the last step; the count and ARIA
      // continue to expose the payment already confirmed by the server.
      this.progressPreparing = true;
      this.visualProgressPercentage = ((this.completedCount - 1) / this.totalCount) * 100;
      this.progressFrame = window.requestAnimationFrame(() => {
        this.progressFrame = window.requestAnimationFrame(() => {
          this.progressFrame = null;
          this.progressPreparing = false;
          this.progressAdvancing = true;
          this.visualProgressPercentage = this.progressPercentage;
          this.progressTimer = window.setTimeout(this.cancelProgressAnimation, PROGRESS_DURATION);
        });
      });
    },
    cancelProgressAnimation() {
      if (this.progressFrame !== null) window.cancelAnimationFrame(this.progressFrame);
      if (this.progressTimer !== null) window.clearTimeout(this.progressTimer);
      this.progressFrame = null;
      this.progressTimer = null;
      this.progressPreparing = false;
      this.progressAdvancing = false;
      this.visualProgressPercentage = null;
    }
  }
}
</script>
